/**
 * AdminImpersonateBar — floating dev/admin debug widget.
 *
 * Visible only to users with `is_admin=True`. Lets the admin preview the
 * UX of each tier (Free / Pro / Clinic) without losing their bypass.
 * The backend continues to treat admin as quota-exempt — this only
 * changes what `effective_tier()` returns for UI gating.
 */
import { useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { setImpersonateTier, getImpersonateTier } from "../lib/api";
import { Eye, X } from "lucide-react";

const TIERS = [
  { id: "none",   label: "Off (admin)" },
  { id: "free",   label: "Free" },
  { id: "pro",    label: "Pro" },
  { id: "clinic", label: "Clinic" },
];

export default function AdminImpersonateBar() {
  const { user } = useAuth();
  const [tier, setTier] = useState(() => getImpersonateTier());
  const [collapsed, setCollapsed] = useState(false);

  const switchTier = useCallback((next) => {
    setImpersonateTier(next);
    setTier(next);
    window.location.reload();
  }, []);

  if (!user?.is_admin) return null;
  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        data-testid="impersonate-expand"
        className="fixed bottom-4 right-4 z-30 w-10 h-10 rounded-full bg-violet-600 text-white shadow-xl flex items-center justify-center hover:bg-violet-500"
        title={`Admin debug · impersonating ${tier === "none" ? "off" : tier}`}
      >
        <Eye className="w-4 h-4" />
      </button>
    );
  }
  return (
    <div
      data-testid="impersonate-bar"
      className="fixed bottom-4 right-4 z-30 bg-slate-900 text-slate-100 rounded-2xl shadow-2xl border border-violet-500/40 p-3 max-w-[280px]"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-violet-300">
          <Eye className="w-3.5 h-3.5" /> Admin debug · impersonate
        </div>
        <button onClick={() => setCollapsed(true)} className="text-slate-400 hover:text-white" data-testid="impersonate-collapse">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="text-[11px] text-slate-400 mb-2 leading-snug">
        Preview the UX of a different tier. You still don&apos;t consume credits.
      </div>
      <div className="grid grid-cols-2 gap-1">
        {TIERS.map((t) => (
          <button
            key={t.id}
            onClick={() => switchTier(t.id)}
            data-testid={`impersonate-${t.id}`}
            className={`min-h-[32px] px-2 rounded-lg text-xs font-medium transition ${
              tier === t.id ? "bg-violet-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tier !== "none" && (
        <div className="mt-2 text-[10px] text-amber-300 text-center">
          Viewing as <span className="font-semibold uppercase">{tier}</span>
        </div>
      )}
    </div>
  );
}
